import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Badge,
    Box,
    Button,
    Select as ChakraSelect,
    Checkbox,
    Divider,
    FormControl,
    FormErrorMessage,
    FormLabel,
    HStack,
    Heading,
    IconButton,
    Image,
    Input,
    SimpleGrid,
    Text,
    VStack,
    useToast,
} from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';
import {
    getAllFacilitiesOutside,
    getPatientById,
    updatePatientFormData,
    validatePatientId,
} from '../../../api';
import TitleHeader from '../../../components/TitleHeader';


// -------------------- CONSTANTS & UTILS --------------------
const STATES = [
    { label: 'Alabama', value: 'AL' }, { label: 'Alaska', value: 'AK' }, { label: 'Arizona', value: 'AZ' },
    { label: 'Arkansas', value: 'AR' }, { label: 'California', value: 'CA' }, { label: 'Colorado', value: 'CO' },
    { label: 'Connecticut', value: 'CT' }, { label: 'Delaware', value: 'DE' }, { label: 'Florida', value: 'FL' },
    { label: 'Georgia', value: 'GA' }, { label: 'Hawaii', value: 'HI' }, { label: 'Idaho', value: 'ID' },
    { label: 'Illinois', value: 'IL' }, { label: 'Indiana', value: 'IN' }, { label: 'Iowa', value: 'IA' },
    { label: 'Kansas', value: 'KS' }, { label: 'Kentucky', value: 'KY' }, { label: 'Louisiana', value: 'LA' },
    { label: 'Maine', value: 'ME' }, { label: 'Maryland', value: 'MD' }, { label: 'Massachusetts', value: 'MA' },
    { label: 'Michigan', value: 'MI' }, { label: 'Minnesota', value: 'MN' }, { label: 'Mississippi', value: 'MS' },
    { label: 'Missouri', value: 'MO' }, { label: 'Montana', value: 'MT' }, { label: 'Nebraska', value: 'NE' },
    { label: 'Nevada', value: 'NV' }, { label: 'New Hampshire', value: 'NH' }, { label: 'New Jersey', value: 'NJ' },
    { label: 'New Mexico', value: 'NM' }, { label: 'New York', value: 'NY' }, { label: 'North Carolina', value: 'NC' },
    { label: 'North Dakota', value: 'ND' }, { label: 'Ohio', value: 'OH' }, { label: 'Oklahoma', value: 'OK' },
    { label: 'Oregon', value: 'OR' }, { label: 'Pennsylvania', value: 'PA' }, { label: 'Rhode Island', value: 'RI' },
    { label: 'South Carolina', value: 'SC' }, { label: 'South Dakota', value: 'SD' }, { label: 'Tennessee', value: 'TN' },
    { label: 'Texas', value: 'TX' }, { label: 'Utah', value: 'UT' }, { label: 'Vermont', value: 'VT' },
    { label: 'Virginia', value: 'VA' }, { label: 'Washington', value: 'WA' }, { label: 'West Virginia', value: 'WV' },
    { label: 'Wisconsin', value: 'WI' }, { label: 'Wyoming', value: 'WY' },
];

const HEADER_BG = 'gray.50';
const HEADER_BG_EXPANDED = 'gray.100';

const digitsOnly = (s) => (s || '').replace(/\D+/g, '');
const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(e || '').trim());

const calcAge = (dobStr) => {
    if (!dobStr) return '';
    const dob = new Date(dobStr);
    if (isNaN(dob)) return '';
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
    return age >= 0 ? String(age) : '';
};

const toYMD = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    if (isNaN(dt)) return '';
    const m = `${dt.getMonth() + 1}`.padStart(2, '0');
    const day = `${dt.getDate()}`.padStart(2, '0');
    return `${dt.getFullYear()}-${m}-${day}`;
};

const imageUrl = (name) => (name ? name : null);

// -------------------- DATA HELPERS --------------------
const mapFacilityOptions = (list = []) =>
    list.map((f) => ({ value: f._id, label: `${f.name} - ${f.address?.city || 'N/A'}` }));

const fetchFacilities = async (setFacilities) => {
    const res = await getAllFacilitiesOutside();
    if (res?.success) {
        const list = res.data?.facilities || [];
        setFacilities(list);
        return list;
    }
    throw new Error(res?.message || 'Failed to load facilities');
};

const fetchPatient = async (id) => {
    const res = await getPatientById(id);
    if (!res?.success) throw new Error(res?.message || 'Failed to load patient');
    const p = res.data?.patient ?? res.data; // supports both shapes
    if (!p) throw new Error('Patient not found');
    return p;
};

const hydrateFromPatient = ({
    patient: p,
    facilities,
    setFacilitySelect,
    setIdentity,
    setAddrStateSelect,
    setGov,
    setGovStateSelect,
    setInsurances,
    setInsPreviews,
}) => {
    // facility select
    const facilitiesOpts = mapFacilityOptions(facilities);
    const facId = typeof p.facility === 'object' ? p.facility?._id : p.facility;
    setFacilitySelect(facilitiesOpts.find((o) => o.value === facId) || null);

    // identity
    setIdentity({
        patientNumber: p.patientNumber || '',
        firstName: p.firstName || '',
        middleName: p.middleName || '',
        lastName: p.lastName || '',
        dob: toYMD(p.dob),
        sex: p.sex || '',
        phone: p.phone || '',
        email: p.email || '',
        addressLine1: p.address?.line1 || '',
        addressLine2: p.address?.line2 || '',
        city: p.address?.city || '',
        state: p.address?.state || '',
        zip: p.address?.zip || '',
    });
    setAddrStateSelect(STATES.find((s) => s.value === (p.address?.state || '')) || null);

    // government ID
    setGov({
        idNumber: p.governmentId?.idNumber || '',
        issuingState: p.governmentId?.issuingState || '',
        expiration: toYMD(p.governmentId?.expiration),
        frontFile: null,
        backFile: null,
        frontExisting: p.governmentId?.frontImageUrl || null,
        backExisting: p.governmentId?.backImageUrl || null,
        removeFront: false,
        removeBack: false,
    });
    setGovStateSelect(STATES.find((s) => s.value === (p.governmentId?.issuingState || '')) || null);

    // insurances (primary + others)
    const insList = [];
    if (p.insurancePrimary) {
        insList.push({
            payerName: p.insurancePrimary.payerName || '',
            planName: p.insurancePrimary.planName || '',
            memberId: p.insurancePrimary.memberId || '',
            groupNo: p.insurancePrimary.groupNo || '',
            subscriber: p.insurancePrimary.subscriber || '',
            relationship: p.insurancePrimary.relationship || '',
            effectiveAt: toYMD(p.insurancePrimary.effectiveAt),
            frontExisting: p.insurancePrimary.cardFrontUrl || null,
            backExisting: p.insurancePrimary.cardBackUrl || null,
            frontFile: null,
            backFile: null,
            removeFront: false,
            removeBack: false,
        });
    }
    (p.insuranceOther || []).forEach((ins) => {
        insList.push({
            payerName: ins.payerName || '',
            planName: ins.planName || '',
            memberId: ins.memberId || '',
            groupNo: ins.groupNo || '',
            subscriber: ins.subscriber || '',
            relationship: ins.relationship || '',
            effectiveAt: toYMD(ins.effectiveAt),
            frontExisting: ins.cardFrontUrl || null,
            backExisting: ins.cardBackUrl || null,
            frontFile: null,
            backFile: null,
            removeFront: false,
            removeBack: false,
        });
    });
    if (insList.length === 0) {
        insList.push({
            payerName: '', planName: '', memberId: '', groupNo: '',
            subscriber: '', relationship: '', effectiveAt: '',
            frontExisting: null, backExisting: null,
            frontFile: null, backFile: null,
            removeFront: false, removeBack: false,
        });
    }
    setInsurances(insList);
    setInsPreviews(insList.map(() => ({ front: null, back: null })));
};

const initEditPage = async (id, setters, toast, setLoading) => {
    setLoading(true);
    try {
        const [facilities, patient] = await Promise.all([
            fetchFacilities(setters.setFacilities),
            fetchPatient(id),
        ]);
        hydrateFromPatient({ patient, facilities, ...setters });
    } catch (e) {
        toast({ title: 'Error', description: e?.message || 'Failed to load', status: 'error', isClosable: true });
    } finally {
        setLoading(false);
    }
};

// -------------------- COMPONENT --------------------
const PatientEditPage = () => {
    const { id } = useParams();
    const toast = useToast();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    // Facilities
    const [facilities, setFacilities] = useState([]);
    const [facilitySelect, setFacilitySelect] = useState(null);

    // Identity & Contact
    const [identity, setIdentity] = useState({
        patientNumber: '',
        firstName: '',
        middleName: '',
        lastName: '',
        dob: '',
        sex: '',
        phone: '',
        email: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zip: '',
    });
    const [addrStateSelect, setAddrStateSelect] = useState(null);
    const age = useMemo(() => calcAge(identity.dob), [identity.dob]);

    // Government ID
    const [gov, setGov] = useState({
        idNumber: '',
        issuingState: '',
        expiration: '',
        frontFile: null,
        backFile: null,
        frontExisting: null,
        backExisting: null,
        removeFront: false,
        removeBack: false,
    });
    const [govStateSelect, setGovStateSelect] = useState(null);
    const [idFrontPreview, setIdFrontPreview] = useState(null);
    const [idBackPreview, setIdBackPreview] = useState(null);

    // Insurances
    const [insurances, setInsurances] = useState([]);
    const [insPreviews, setInsPreviews] = useState([]);

    // Duplicate check for Gov ID
    const [idCheck, setIdCheck] = useState({ loading: false, exists: false, count: 0, message: '' });
    const checkTimer = useRef(null);
    const lastQueryRef = useRef('');

    // Object URL revoke queue
    const revokeQueue = useRef([]);

    // Accordion state — all collapsed by default
    // Top-level sections: 0 Facility, 1 Identity, 2 Gov ID, 3 Insurances, 4 Review
    const [accIdx, setAccIdx] = useState([]);
    // Nested insurance accordion indices — also collapsed by default
    const [insAccIdx, setInsAccIdx] = useState([]);

    // ------- INIT -------
    useEffect(() => {
        initEditPage(
            id,
            {
                setFacilities,
                setFacilitySelect,
                setIdentity,
                setAddrStateSelect,
                setGov,
                setGovStateSelect,
                setInsurances,
                setInsPreviews,
            },
            toast,
            setLoading
        );
    }, [id]);

    // Keep nested insurance indices valid when list size changes, without auto-opening new ones
    useEffect(() => {
        setInsAccIdx((prev) => prev.filter((i) => i < insurances.length));
    }, [insurances.length]);

    // ------- Previews for chosen files -------
    useEffect(() => {
        if (gov.frontFile) {
            const url = URL.createObjectURL(gov.frontFile);
            setIdFrontPreview((prev) => { if (prev) revokeQueue.current.push(prev); return url; });
        } else {
            setIdFrontPreview(null);
        }
    }, [gov.frontFile]);

    useEffect(() => {
        if (gov.backFile) {
            const url = URL.createObjectURL(gov.backFile);
            setIdBackPreview((prev) => { if (prev) revokeQueue.current.push(prev); return url; });
        } else {
            setIdBackPreview(null);
        }
    }, [gov.backFile]);

    useEffect(() => {
        setInsPreviews((prev) => {
            const next = insurances.map((ins, idx) => {
                const obj = prev[idx] ? { ...prev[idx] } : { front: null, back: null };
                if (ins.frontFile) {
                    const url = URL.createObjectURL(ins.frontFile);
                    if (obj.front) revokeQueue.current.push(obj.front);
                    obj.front = url;
                } else {
                    obj.front = null;
                }
                if (ins.backFile) {
                    const url = URL.createObjectURL(ins.backFile);
                    if (obj.back) revokeQueue.current.push(obj.back);
                    obj.back = url;
                } else {
                    obj.back = null;
                }
                return obj;
            });
            return next;
        });
    }, [insurances]);

    useEffect(() => () => {
        revokeQueue.current.forEach((u) => { try { URL.revokeObjectURL(u); } catch { } });
        if (idFrontPreview) try { URL.revokeObjectURL(idFrontPreview); } catch { }
        if (idBackPreview) try { URL.revokeObjectURL(idBackPreview); } catch { }
        insPreviews.forEach((p) => {
            if (p?.front) try { URL.revokeObjectURL(p.front); } catch { }
            if (p?.back) try { URL.revokeObjectURL(p.back); } catch { }
        });
    }, []);

    // ------- Debounced duplicate check (facility + id + state) -------
    useEffect(() => {
        const facilityId = facilitySelect?.value;
        const idNumber = (gov.idNumber || '').trim();
        const issuingState = (gov.issuingState || '').trim();

        if (checkTimer.current) clearTimeout(checkTimer.current);
        if (!facilityId || !idNumber || !issuingState) {
            setIdCheck({ loading: false, exists: false, count: 0, message: '' });
            return;
        }

        const key = `${facilityId}|${idNumber}|${issuingState}`;
        lastQueryRef.current = key;
        setIdCheck((s) => ({ ...s, loading: true }));

        checkTimer.current = setTimeout(async () => {
            try {
                const res = await validatePatientId({ idNumber, issuingState, facilityId, excludeId: id });
                if (lastQueryRef.current !== key) return;
                if (res?.success) {
                    const exists = !!res.data?.duplicate;
                    setIdCheck({
                        loading: false,
                        exists,
                        count: res.data?.count || (exists ? 1 : 0),
                        message: exists ? 'A patient with this ID already exists in this facility.' : ''
                    });
                } else {
                    setIdCheck({ loading: false, exists: false, count: 0, message: '' });
                }
            } catch {
                setIdCheck({ loading: false, exists: false, count: 0, message: '' });
            }
        }, 400);

        return () => { if (checkTimer.current) clearTimeout(checkTimer.current); };
    }, [facilitySelect?.value, gov.idNumber, gov.issuingState]);

    // -------------------- VALIDATION & SUBMIT --------------------
    const req = (v) => v && String(v).trim() !== '';
    const toastErr = (msg) => { toast({ title: 'Validation', description: msg, status: 'error', duration: 2200, isClosable: true }); return false; };

    const validate = () => {
        if (!facilitySelect?.value) return toastErr('Facility is required');
        const { firstName, lastName, dob, sex, phone, email, addressLine1, city, state, zip } = identity;
        if (!req(firstName)) return toastErr('First Name is required');
        if (!req(lastName)) return toastErr('Last Name is required');
        if (!req(dob)) return toastErr('DOB is required');
        if (!req(sex)) return toastErr('Sex is required');

        const phoneDigits = digitsOnly(phone);
        if (!req(phoneDigits)) return toastErr('Phone is required');
        if (phoneDigits.length < 10 || phoneDigits.length > 15) return toastErr('Phone must be 10–15 digits');

        if (!req(email)) return toastErr('Email is required');
        if (!isValidEmail(email)) return toastErr('Invalid email');

        if (!req(addressLine1)) return toastErr('Address Line 1 is required');
        if (!req(city)) return toastErr('City is required');
        if (!req(state)) return toastErr('State is required');
        if (!req(zip)) return toastErr('ZIP is required');

        if (idCheck.exists) return toastErr('Duplicate ID number in this facility');
        return true;
    };

    const addInsurance = () => {
        setInsurances((prev) => [
            ...prev,
            {
                payerName: '', planName: '', memberId: '', groupNo: '',
                subscriber: '', relationship: '', effectiveAt: '',
                frontExisting: null, backExisting: null,
                frontFile: null, backFile: null,
                removeFront: false, removeBack: false,
            },
        ]);
        setInsPreviews((prev) => [...prev, { front: null, back: null }]);
    };

    const removeInsurance = (idx) => {
        setInsurances((prev) => prev.filter((_, i) => i !== idx));
        setInsPreviews((prev) => prev.filter((_, i) => i !== idx));
    };

    const submit = async () => {
        if (!validate()) return;
        setSubmitting(true);
        try {
            const fd = new FormData();

            // facility
            fd.append('facility', facilitySelect.value);

            // identity/contact
            const phoneDigits = digitsOnly(identity.phone);
            const idy = {
                patientNumber: identity.patientNumber,
                firstName: identity.firstName,
                middleName: identity.middleName,
                lastName: identity.lastName,
                dob: identity.dob,
                sex: identity.sex,
                phone: phoneDigits,
                email: identity.email,
                addressLine1: identity.addressLine1,
                addressLine2: identity.addressLine2,
                city: identity.city,
                state: identity.state,
                zip: identity.zip,
            };
            Object.entries(idy).forEach(([k, v]) => {
                if (v !== undefined && v !== null && String(v).trim() !== '') fd.append(k, v);
            });

            // Government ID text
            const idNum = (gov.idNumber || '').trim();
            const issState = (gov.issuingState || '').trim();
            const exp = (gov.expiration || '').trim();
            fd.append('govIdNumber', idNum);
            fd.append('govIssuingState', issState);
            fd.append('govExpiration', exp);
            fd.append('governmentId[idNumber]', idNum);
            fd.append('governmentId[issuingState]', issState);
            fd.append('governmentId[expiration]', exp);

            // explicit removal flags for DL
            if (gov.removeFront) fd.append('removeGovFront', 'true');
            if (gov.removeBack) fd.append('removeGovBack', 'true');

            const docTypes = [];

            // DL uploads (replace)
            if (gov.frontFile) { fd.append('documents', gov.frontFile); docTypes.push('idFront'); }
            if (gov.backFile) { fd.append('documents', gov.backFile); docTypes.push('idBack'); }

            // Insurances JSON payload (include remove flags)
            const insPayload = insurances.map((ins) => ({
                payerName: ins.payerName,
                planName: ins.planName,
                memberId: ins.memberId,
                groupNo: ins.groupNo,
                subscriber: ins.subscriber,
                relationship: ins.relationship,
                effectiveAt: ins.effectiveAt,
                removeFront: !!ins.removeFront,
                removeBack: !!ins.removeBack,
            }));
            const insJson = JSON.stringify(insPayload);
            fd.append('insurances', insJson);
            fd.append('insurancesJson', insJson);

            // Primary explicit flags (optional convenience for backend)
            if (insurances[0]?.removeFront) fd.append('removePrimaryFront', 'true');
            if (insurances[0]?.removeBack) fd.append('removePrimaryBack', 'true');

            // Insurance uploads (positional: 0 = primary, others follow)
            insurances.forEach((ins) => {
                if (ins.frontFile) { fd.append('documents', ins.frontFile); docTypes.push('insFront'); }
                if (ins.backFile) { fd.append('documents', ins.backFile); docTypes.push('insBack'); }
            });

            if (docTypes.length) {
                fd.append('docTypes', docTypes.join(','));
                docTypes.forEach((t) => fd.append('docTypes[]', t));
            }

            const res = await updatePatientFormData(id, fd);
            if (!res?.success) throw new Error(res?.message || 'Update failed');

            toast({ title: 'Patient updated', status: 'success', duration: 2000, isClosable: true });
            navigate('/reqs/patients');
        } catch (e) {
            toast({ title: 'Error', description: e.message || 'Failed to update', status: 'error', duration: 3000, isClosable: true });
        } finally {
            setSubmitting(false);
        }
    };

    // -------------------- RENDER --------------------
    if (loading) {
        return (
            <Box maxW="6xl" mx="auto" p={8}>
                <Heading size="md">Loading…</Heading>
            </Box>
        );
    }

    const isFacilityOpen = accIdx.includes(0);

    return (
        <Box mx="auto" p={8}>
            <TitleHeader title="Update Patient Details" />

            <Divider my={4} />

            <Accordion
                index={accIdx}
                onChange={(val) => setAccIdx(Array.isArray(val) ? val : [val])}
                allowMultiple
                reduceMotion
            >
                {/* Facility */}
                <AccordionItem mb={5} border="none">
                    <h2>
                        <AccordionButton
                            bg={HEADER_BG_EXPANDED}
                            _hover={{ bg: isFacilityOpen ? HEADER_BG_EXPANDED : HEADER_BG }}
                            _expanded={{ bg: HEADER_BG_EXPANDED }}
                            borderRadius="md"
                            px={4}
                            py={3}
                            boxShadow="sm"
                            mb={5}
                            mt={5}
                        >
                            <HStack flex="1" textAlign="left" spacing={3}>
                                <Heading size="sm">Facility</Heading>
                                <Badge>Required</Badge>
                                {!isFacilityOpen && facilitySelect?.label && (
                                    <Text fontSize="sm" color="gray.600" noOfLines={1}>
                                        • {facilitySelect.label}
                                    </Text>
                                )}
                            </HStack>
                            <AccordionIcon />
                        </AccordionButton>
                    </h2>
                    <AccordionPanel pb={4}>
                        <VStack align="stretch" spacing={4}>
                            <FormControl isRequired>
                                <FormLabel>Facility</FormLabel>
                                <Select
                                    options={mapFacilityOptions(facilities)}
                                    value={facilitySelect}
                                    onChange={(opt) => setFacilitySelect(opt)}
                                    placeholder="Select facility"
                                    isClearable
                                />
                            </FormControl>
                        </VStack>
                    </AccordionPanel>
                </AccordionItem>

                {/* Identity & Contact */}
                <AccordionItem mb={5} border="none">
                    <h2>
                        <AccordionButton
                            bg={HEADER_BG_EXPANDED}
                            _hover={{ bg: accIdx.includes(1) ? HEADER_BG_EXPANDED : HEADER_BG }}
                            _expanded={{ bg: HEADER_BG_EXPANDED }}
                            borderRadius="md"
                            px={4}
                            py={3}
                            boxShadow="sm"
                            mb={5}
                            mt={5}
                        >
                            <HStack flex="1" textAlign="left" spacing={3}>
                                <Heading size="sm">Identity & Contact</Heading>
                            </HStack>
                            <AccordionIcon />
                        </AccordionButton>
                    </h2>
                    <AccordionPanel pb={4}>
                        <VStack spacing={6} align="stretch">
                            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                                <FormControl isRequired>
                                    <FormLabel>First Name</FormLabel>
                                    <Input value={identity.firstName} onChange={(e) => setIdentity((s) => ({ ...s, firstName: e.target.value }))} />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Middle Name or M.I (optional)</FormLabel>
                                    <Input value={identity.middleName} onChange={(e) => setIdentity((s) => ({ ...s, middleName: e.target.value }))} />
                                </FormControl>
                                <FormControl isRequired>
                                    <FormLabel>Last Name</FormLabel>
                                    <Input value={identity.lastName} onChange={(e) => setIdentity((s) => ({ ...s, lastName: e.target.value }))} />
                                </FormControl>

                                <FormControl isRequired>
                                    <FormLabel>Date of Birth</FormLabel>
                                    <HStack>
                                        <Input type="date" value={identity.dob} onChange={(e) => setIdentity((s) => ({ ...s, dob: e.target.value }))} />
                                        <Text minW="60px" textAlign="right"><b>Age:</b> {age || '—'}</Text>
                                    </HStack>
                                </FormControl>

                                <FormControl isRequired>
                                    <FormLabel>Sex</FormLabel>
                                    <ChakraSelect value={identity.sex} onChange={(e) => setIdentity((s) => ({ ...s, sex: e.target.value }))}>
                                        <option value="">Select</option>
                                        <option value="M">Male</option>
                                        <option value="F">Female</option>
                                        <option value="X">Non-binary/Unspecified</option>
                                        <option value="U">Unknown</option>
                                    </ChakraSelect>
                                </FormControl>

                                <FormControl isRequired>
                                    <FormLabel>Phone</FormLabel>
                                    <Input
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        value={identity.phone}
                                        onChange={(e) => setIdentity((s) => ({ ...s, phone: digitsOnly(e.target.value).slice(0, 15) }))}
                                    />
                                </FormControl>

                                <FormControl isRequired>
                                    <FormLabel>Email</FormLabel>
                                    <Input
                                        type="email"
                                        value={identity.email}
                                        onChange={(e) => setIdentity((s) => ({ ...s, email: e.target.value }))}
                                        onBlur={() => {
                                            if (identity.email && !isValidEmail(identity.email)) {
                                                toast({ title: 'Invalid email', status: 'warning', duration: 2000, isClosable: true });
                                            }
                                        }}
                                    />
                                </FormControl>
                            </SimpleGrid>

                            <Heading size="sm" mt={2}>Contact</Heading>
                            <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6}>
                                <FormControl isRequired><FormLabel>Address Line 1</FormLabel><Input value={identity.addressLine1} onChange={(e) => setIdentity((s) => ({ ...s, addressLine1: e.target.value }))} /></FormControl>
                                <FormControl><FormLabel>Address Line 2 (optional)</FormLabel><Input value={identity.addressLine2} onChange={(e) => setIdentity((s) => ({ ...s, addressLine2: e.target.value }))} /></FormControl>
                                <FormControl isRequired><FormLabel>City</FormLabel><Input value={identity.city} onChange={(e) => setIdentity((s) => ({ ...s, city: e.target.value }))} /></FormControl>
                                <FormControl isRequired>
                                    <FormLabel>State</FormLabel>
                                    <Select
                                        options={STATES}
                                        value={addrStateSelect}
                                        onChange={(opt) => {
                                            setAddrStateSelect(opt);
                                            setIdentity((s) => ({ ...s, state: opt ? opt.value : '' }));
                                        }}
                                        placeholder="Select state"
                                        isClearable
                                    />
                                </FormControl>
                                <FormControl isRequired><FormLabel>ZIP</FormLabel><Input value={identity.zip} onChange={(e) => setIdentity((s) => ({ ...s, zip: e.target.value }))} /></FormControl>
                            </SimpleGrid>
                        </VStack>
                    </AccordionPanel>
                </AccordionItem>

                {/* Government ID */}
                <AccordionItem mb={5} border="none">
                    <h2>
                        <AccordionButton
                            bg={HEADER_BG_EXPANDED}
                            _hover={{ bg: accIdx.includes(2) ? HEADER_BG_EXPANDED : HEADER_BG }}
                            _expanded={{ bg: HEADER_BG_EXPANDED }}
                            borderRadius="md"
                            px={4}
                            py={3}
                            boxShadow="sm"
                            mb={5}
                            mt={5}
                        >
                            <HStack flex="1" textAlign="left" spacing={3}>
                                <Heading size="sm">Government ID</Heading>
                            </HStack>
                            <AccordionIcon />
                        </AccordionButton>
                    </h2>
                    <AccordionPanel pb={4}>
                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                            <FormControl isRequired isInvalid={idCheck.exists}>
                                <FormLabel>ID / License Number</FormLabel>
                                <Input
                                    value={gov.idNumber}
                                    onChange={(e) => setGov((s) => ({ ...s, idNumber: e.target.value }))}
                                    onBlur={() => setGov((s) => ({ ...s, idNumber: (s.idNumber || '').trim() }))}
                                />
                                {idCheck.loading && <Text fontSize="xs" color="gray.500" mt={1}>Checking…</Text>}
                                <FormErrorMessage>{idCheck.message || 'Duplicate ID number in this facility.'}</FormErrorMessage>
                            </FormControl>
                            <FormControl isRequired>
                                <FormLabel>Issuing State</FormLabel>
                                <Select
                                    options={STATES}
                                    value={govStateSelect}
                                    onChange={(opt) => { setGovStateSelect(opt); setGov((s) => ({ ...s, issuingState: opt ? opt.value : '' })); }}
                                    placeholder="Select state"
                                    isClearable
                                />
                            </FormControl>
                            <FormControl isRequired>
                                <FormLabel>Expiration</FormLabel>
                                <Input type="date" value={gov.expiration} onChange={(e) => setGov((s) => ({ ...s, expiration: e.target.value }))} />
                            </FormControl>

                            {/* File controls + previews */}
                            <Box gridColumn={{ md: '1 / -1' }}>
                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                                    <Box>
                                        <FormControl>
                                            <FormLabel>ID Front (replace)</FormLabel>
                                            <Input type="file" accept="image/*" onChange={(e) => setGov((s) => ({ ...s, frontFile: e.target.files?.[0] || null }))} />
                                        </FormControl>
                                        <HStack mt={2} spacing={4} align="start">
                                            {idFrontPreview ? (
                                                <Image src={idFrontPreview} alt="New Front" maxH="120px" borderRadius="md" />
                                            ) : gov.frontExisting && !gov.removeFront ? (
                                                <Image src={imageUrl(gov.frontExisting)} alt="Current Front" maxH="120px" borderRadius="md" />
                                            ) : (
                                                <Text>Front: —</Text>
                                            )}
                                            {gov.frontExisting && !gov.frontFile && (
                                                <Checkbox isChecked={gov.removeFront} onChange={(e) => setGov((s) => ({ ...s, removeFront: e.target.checked }))}>
                                                    Remove current
                                                </Checkbox>
                                            )}
                                        </HStack>
                                    </Box>
                                    <Box>
                                        <FormControl>
                                            <FormLabel>ID Back (replace)</FormLabel>
                                            <Input type="file" accept="image/*" onChange={(e) => setGov((s) => ({ ...s, backFile: e.target.files?.[0] || null }))} />
                                        </FormControl>
                                        <HStack mt={2} spacing={4} align="start">
                                            {idBackPreview ? (
                                                <Image src={idBackPreview} alt="New Back" maxH="120px" borderRadius="md" />
                                            ) : gov.backExisting && !gov.removeBack ? (
                                                <Image src={imageUrl(gov.backExisting)} alt="Current Back" maxH="120px" borderRadius="md" />
                                            ) : (
                                                <Text>Back: —</Text>
                                            )}
                                            {gov.backExisting && !gov.backFile && (
                                                <Checkbox isChecked={gov.removeBack} onChange={(e) => setGov((s) => ({ ...s, removeBack: e.target.checked }))}>
                                                    Remove current
                                                </Checkbox>
                                            )}
                                        </HStack>
                                    </Box>
                                </SimpleGrid>
                            </Box>
                        </SimpleGrid>
                    </AccordionPanel>
                </AccordionItem>

                {/* Insurances */}
                <AccordionItem mb={5} border="none">
                    <h2>
                        <AccordionButton
                            bg={HEADER_BG_EXPANDED}
                            _hover={{ bg: accIdx.includes(3) ? HEADER_BG_EXPANDED : HEADER_BG }}
                            _expanded={{ bg: HEADER_BG_EXPANDED }}
                            borderRadius="md"
                            px={4}
                            py={3}
                            boxShadow="sm"
                            mb={5}
                            mt={5}
                        >
                            <HStack flex="1" textAlign="left" spacing={3}>
                                <Heading size="sm">Insurance(s)</Heading>
                            </HStack>
                            <AccordionIcon />
                        </AccordionButton>
                    </h2>
                    <AccordionPanel pb={4}>
                        <Accordion
                            index={insAccIdx}
                            onChange={(val) => setInsAccIdx(Array.isArray(val) ? val : [val])}
                            allowMultiple
                            reduceMotion
                        >
                            {insurances.map((ins, idx) => (
                                <AccordionItem key={idx}>
                                    <h3>
                                        <AccordionButton>
                                            <HStack flex="1" textAlign="left" py={1}>
                                                <Heading size="xs">{idx === 0 ? 'Primary Insurance' : `Insurance #${idx + 1}`}</Heading>
                                            </HStack>
                                            <HStack>
                                                {insurances.length > 1 && (
                                                    <IconButton
                                                        aria-label="Remove"
                                                        icon={<DeleteIcon />}
                                                        size="xs"
                                                        colorScheme="red"
                                                        mr={2}
                                                        onClick={(e) => { e.stopPropagation(); removeInsurance(idx); }}
                                                    />
                                                )}
                                                <AccordionIcon />
                                            </HStack>
                                        </AccordionButton>
                                    </h3>
                                    <AccordionPanel pb={4}>
                                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                                            <FormControl isRequired><FormLabel>Payer Name</FormLabel><Input value={ins.payerName} onChange={(e) => setInsurances((arr) => arr.map((x, i) => (i === idx ? { ...x, payerName: e.target.value } : x)))} /></FormControl>
                                            <FormControl isRequired><FormLabel>Plan Name</FormLabel><Input value={ins.planName} onChange={(e) => setInsurances((arr) => arr.map((x, i) => (i === idx ? { ...x, planName: e.target.value } : x)))} /></FormControl>
                                            <FormControl isRequired><FormLabel>Member ID</FormLabel><Input value={ins.memberId} onChange={(e) => setInsurances((arr) => arr.map((x, i) => (i === idx ? { ...x, memberId: e.target.value } : x)))} /></FormControl>
                                            <FormControl isRequired><FormLabel>Group No</FormLabel><Input value={ins.groupNo} onChange={(e) => setInsurances((arr) => arr.map((x, i) => (i === idx ? { ...x, groupNo: e.target.value } : x)))} /></FormControl>
                                            <FormControl isRequired><FormLabel>Subscriber</FormLabel><Input value={ins.subscriber} onChange={(e) => setInsurances((arr) => arr.map((x, i) => (i === idx ? { ...x, subscriber: e.target.value } : x)))} /></FormControl>
                                            <FormControl isRequired><FormLabel>Relationship</FormLabel><Input value={ins.relationship} onChange={(e) => setInsurances((arr) => arr.map((x, i) => (i === idx ? { ...x, relationship: e.target.value } : x)))} /></FormControl>
                                            <FormControl isRequired><FormLabel>Effective At</FormLabel><Input type="date" value={ins.effectiveAt} onChange={(e) => setInsurances((arr) => arr.map((x, i) => (i === idx ? { ...x, effectiveAt: e.target.value } : x)))} /></FormControl>

                                            {/* Uploads */}
                                            <FormControl>
                                                <FormLabel>Card Front (replace)</FormLabel>
                                                <Input type="file" accept="image/*" onChange={(e) => setInsurances((arr) => arr.map((x, i) => (i === idx ? { ...x, frontFile: e.target.files?.[0] || null } : x)))} />
                                            </FormControl>
                                            <FormControl>
                                                <FormLabel>Card Back (replace)</FormLabel>
                                                <Input type="file" accept="image/*" onChange={(e) => setInsurances((arr) => arr.map((x, i) => (i === idx ? { ...x, backFile: e.target.files?.[0] || null } : x)))} />
                                            </FormControl>
                                        </SimpleGrid>

                                        <HStack spacing={6} mt={3} align="start">
                                            {insPreviews[idx]?.front ? (
                                                <Image src={insPreviews[idx].front} alt={`New Front ${idx}`} maxH="140px" borderRadius="md" />
                                            ) : ins.frontExisting && !ins.removeFront ? (
                                                <Image src={imageUrl(ins.frontExisting)} alt={`Current Front ${idx}`} maxH="140px" borderRadius="md" />
                                            ) : (
                                                <Text>Front: —</Text>
                                            )}

                                            {insPreviews[idx]?.back ? (
                                                <Image src={insPreviews[idx].back} alt={`New Back ${idx}`} maxH="140px" borderRadius="md" />
                                            ) : ins.backExisting && !ins.removeBack ? (
                                                <Image src={imageUrl(ins.backExisting)} alt={`Current Back ${idx}`} maxH="140px" borderRadius="md" />
                                            ) : (
                                                <Text>Back: —</Text>
                                            )}

                                            <VStack align="start" spacing={2}>
                                                {ins.frontExisting && !ins.frontFile && (
                                                    <Checkbox isChecked={ins.removeFront} onChange={(e) => setInsurances((arr) => arr.map((x, i) => (i === idx ? { ...x, removeFront: e.target.checked } : x)))}>
                                                        Remove front
                                                    </Checkbox>
                                                )}
                                                {ins.backExisting && !ins.backFile && (
                                                    <Checkbox isChecked={ins.removeBack} onChange={(e) => setInsurances((arr) => arr.map((x, i) => (i === idx ? { ...x, removeBack: e.target.checked } : x)))}>
                                                        Remove back
                                                    </Checkbox>
                                                )}
                                            </VStack>
                                        </HStack>
                                    </AccordionPanel>
                                </AccordionItem>
                            ))}
                        </Accordion>

                        <Button leftIcon={<AddIcon />} variant="outline" mt={3} onClick={addInsurance}>
                            Add Insurance
                        </Button>
                    </AccordionPanel>
                </AccordionItem>

                {/* Review */}
                <AccordionItem mb={5} border="none">
                    <h2>
                        <AccordionButton
                            bg={HEADER_BG_EXPANDED}
                            _hover={{ HEADER_BG }}
                            _expanded={{ bg: HEADER_BG_EXPANDED }}
                            borderRadius="md"
                            px={4}
                            py={3}
                            boxShadow="sm"
                            mb={5}
                            mt={5}
                        >
                            <HStack flex="1" textAlign="left" spacing={3}>
                                <Heading size="sm">Review</Heading>
                            </HStack>
                            <AccordionIcon />
                        </AccordionButton>
                    </h2>
                    <AccordionPanel pb={4}>
                        <VStack spacing={6} align="stretch">
                            <Box borderWidth="1px" borderRadius="md" p={4}>
                                <Heading size="sm" mb={3}>Facility</Heading>
                                <Text>{facilitySelect?.label || '-'}</Text>
                            </Box>

                            <Box borderWidth="1px" borderRadius="md" p={4}>
                                <Heading size="sm" mb={3}>Identity & Contact</Heading>
                                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                                    <Text><b>Name:</b> {[identity.firstName, identity.middleName, identity.lastName].filter(Boolean).join(' ')}</Text>
                                    <Text><b>DOB / Age / Sex:</b> {(identity.dob || '—')} / {(age || '—')} / {(identity.sex || '—')}</Text>
                                    <Text><b>Phone:</b> {digitsOnly(identity.phone) || '—'}</Text>
                                    <Text><b>Email:</b> {identity.email || '—'}</Text>
                                    <Text><b>Address:</b> {[identity.addressLine1, identity.addressLine2, identity.city, identity.state, identity.zip].filter(Boolean).join(', ') || '—'}</Text>
                                </SimpleGrid>
                            </Box>

                            <Box borderWidth="1px" borderRadius="md" p={4}>
                                <Heading size="sm" mb={3}>Government ID</Heading>
                                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} mb={3}>
                                    <Text><b>ID #:</b> {gov.idNumber || '—'}</Text>
                                    <Text><b>Issuing State:</b> {gov.issuingState || '—'}</Text>
                                    <Text><b>Expiration:</b> {gov.expiration || '—'}</Text>
                                </SimpleGrid>
                                <HStack spacing={6}>
                                    {idFrontPreview
                                        ? <Image src={idFrontPreview} alt="ID Front" maxH="140px" borderRadius="md" />
                                        : (gov.frontExisting && !gov.removeFront
                                            ? <Image src={imageUrl(gov.frontExisting)} alt="ID Front" maxH="140px" borderRadius="md" />
                                            : <Text>Front: —</Text>)}
                                    {idBackPreview
                                        ? <Image src={idBackPreview} alt="ID Back" maxH="140px" borderRadius="md" />
                                        : (gov.backExisting && !gov.removeBack
                                            ? <Image src={imageUrl(gov.backExisting)} alt="ID Back" maxH="140px" borderRadius="md" />
                                            : <Text>Back: —</Text>)}
                                </HStack>
                            </Box>

                            <Box borderWidth="1px" borderRadius="md" p={4}>
                                <Heading size="sm" mb={3}>Insurances</Heading>
                                {insurances.map((ins, i) => (
                                    <SimpleGrid key={i} columns={{ base: 1, md: 3 }} spacing={3} mb={3}>
                                        <Text><b>{i === 0 ? 'Primary' : `Insurance #${i + 1}`}</b></Text>
                                        <Text><b>Payer:</b> {ins.payerName || '—'}</Text>
                                        <Text><b>Plan:</b> {ins.planName || '—'}</Text>
                                        <Text><b>Member ID:</b> {ins.memberId || '—'}</Text>
                                        <Text><b>Group No:</b> {ins.groupNo || '—'}</Text>
                                        <Text><b>Subscriber:</b> {ins.subscriber || '—'}</Text>
                                        <Text><b>Relationship:</b> {ins.relationship || '—'}</Text>
                                        <Text><b>Effective At:</b> {ins.effectiveAt || '—'}</Text>
                                    </SimpleGrid>
                                ))}
                            </Box>

                            <HStack justify="space-between" mt={2}>
                                <Button onClick={() => navigate(-1)}>Cancel</Button>
                                <Button colorScheme="teal" isLoading={submitting} onClick={submit} isDisabled={idCheck.loading || idCheck.exists}>
                                    Save Changes
                                </Button>
                            </HStack>
                        </VStack>
                    </AccordionPanel>
                </AccordionItem>
            </Accordion>
        </Box>
    );
};

export default PatientEditPage;
